// blackhole/rig.js: camera path, tiers and small math helpers for blackhole.js.
// Everything here is a pure function of (progress, look, params, aspect).
import { clamp } from '../lib/common.js';

// Monotone cubic (Fritsch-Carlson) through [[x, y], ...]: C1, no overshoot.
export function curve(keys) {
  const n = keys.length, xs = keys.map((k) => k[0]), ys = keys.map((k) => k[1]);
  const d = [], m = new Array(n).fill(0);
  for (let i = 0; i < n - 1; i++) d.push((ys[i + 1] - ys[i]) / (xs[i + 1] - xs[i]));
  m[0] = d[0]; m[n - 1] = d[n - 2];
  for (let i = 1; i < n - 1; i++) m[i] = d[i - 1] * d[i] <= 0 ? 0 : (d[i - 1] + d[i]) / 2;
  for (let i = 0; i < n - 1; i++) {
    if (d[i] === 0) { m[i] = 0; m[i + 1] = 0; continue; }
    const a = m[i] / d[i], b = m[i + 1] / d[i], s = a * a + b * b;
    if (s > 9) { const t = 3 / Math.sqrt(s); m[i] = t * a * d[i]; m[i + 1] = t * b * d[i]; }
  }
  return (x) => {
    if (x <= xs[0]) return ys[0];
    if (x >= xs[n - 1]) return ys[n - 1];
    let i = 0;
    while (x > xs[i + 1]) i++;
    const h = xs[i + 1] - xs[i], t = (x - xs[i]) / h, t2 = t * t, t3 = t2 * t;
    return (2 * t3 - 3 * t2 + 1) * ys[i] + (t3 - 2 * t2 + t) * h * m[i]
      + (-2 * t3 + 3 * t2) * ys[i + 1] + (t3 - t2) * h * m[i + 1];
  };
}

// Page & Thorne (1974) flux for a = 0, closed form (same as the shader).
export function pageThorne(r) {
  const x = Math.sqrt(r), x0 = Math.sqrt(6), s3 = Math.sqrt(3);
  const lg = Math.log(Math.max(((x - s3) * (x0 + s3)) / ((x + s3) * (x0 - s3)), 1e-6));
  return Math.max((x - x0 - 0.5 * s3 * lg) / (x ** 5 * (r - 3)), 0);
}

// Per-tier budgets. res = internal resolution in CSS px; maxPx caps huge screens.
// k / kin: integrator step (dt = k r), validated in tools/blackhole_physics.py.
export const BH_TIERS = {
  high: { steps: 300, stars: 4, hits: 4, res: 0.9, maxPx: 1.25e6, k: 0.06, kin: 0.035, disk: [2048, 1024], sky: 1024, mw: 'mw-4k.jpg' },
  mid: { steps: 210, stars: 3, hits: 3, res: 0.7, maxPx: 0.55e6, k: 0.08, kin: 0.05, disk: [2048, 1024], sky: 1024, mw: 'mw-2k.jpg' },
  low: { steps: 150, stars: 2, hits: 3, res: 0.55, maxPx: 0.3e6, k: 0.1, kin: 0.06, disk: [1024, 512], sky: 512, mw: 'mw-2k.jpg' },
};

const D2R = Math.PI / 180;

// The film, in progress units (0..1):
//   0.00-0.10 ARRIVAL   a small shadow + Einstein ring against the galactic centre
//   0.06-0.30 IGNITION  the disk lights up from the inside out as we close in
//   0.30-0.68 HERO      near edge-on, lensed disk arching over and under the shadow
//   0.68-0.90 PLUNGE    radial fall, spiralling, the disk sweeping past
//   0.86-0.96 LOOK BACK the camera turns: the sky shrinks to a blue-shifted circle
//   0.96-1.00 DARK      fade to black at the horizon
const LOGR = curve([
  [0.0, Math.log(420)], [0.08, Math.log(260)], [0.18, Math.log(140)], [0.3, Math.log(88)],
  [0.5, Math.log(74)], [0.68, Math.log(62)], [0.76, Math.log(36)], [0.82, Math.log(16)],
  [0.86, Math.log(7.5)], [0.89, Math.log(3.6)], [0.915, Math.log(2.8)], [0.94, Math.log(2.4)],
  [0.965, Math.log(2.12)], [1.0, Math.log(2.006)],
]);
// hero: a slow drift from above the plane down to a grazing view, so the
// lensed arc over the shadow visibly reshapes while the copy is on screen
const EL = curve([[0, 12], [0.12, 11], [0.3, 9.5], [0.5, 6.5], [0.7, 3.5], [0.84, 10], [1.0, 16]]);
const AZ = curve([[0, 0], [0.3, 6], [0.68, 16], [0.88, 34], [1.0, 40]]);
const FOV = curve([[0, 40], [0.12, 30], [0.3, 20], [0.6, 18.5], [0.68, 19], [0.78, 30], [0.84, 42], [0.9, 50], [1.0, 50]]);

export const smooth01 = (a, b, v) => { const t = clamp((v - a) / (b - a), 0, 1); return t * t * (3 - 2 * t); };

// Camera state for a progress value. look: {x, y} in -1..1.
export function rigAt(p, look, params, aspect, out) {
  p = clamp(p, 0, 1);
  const lx = params.lookX !== undefined ? +params.lookX : look.x;
  const ly = params.lookY !== undefined ? +params.lookY : look.y;
  const plunge = smooth01(0.7, 0.9, p);
  const lookK = 1 - 0.75 * plunge;             // less steering once we are falling
  out.r = Math.exp(LOGR(p));
  // the disk is axisymmetric, so tilt (look.y) is what visibly changes the
  // image; it may go below the plane (the mirrored view) but not to the pole
  out.el = clamp(EL(p) + ly * 13 * lookK + (+params.tilt || 0), -35, 80) * D2R;
  out.az = (AZ(p) + lx * 32 * lookK + (+params.orbit || 0)) * D2R;
  let tanV = Math.tan(FOV(p) * 0.5 * D2R);
  // portrait: keep ~90% of the desktop (aspect 1.6) horizontal coverage
  if (aspect < 1.6) tanV = Math.max(tanV, 0.9 * tanV * 1.6 / aspect);
  out.tanHalf = Math.min(tanV, 1.6);
  // the dive: tilt away from the hole so the shadow's rim stays in frame.
  // Target tilt = shadow half-angle + 0.5 half-fov (rim ~25% up the frame);
  // it saturates at 180 deg (looking straight back out) near the horizon.
  const B_C = 3 * Math.sqrt(3);
  const sArg = Math.min(1, B_C * Math.sqrt(Math.max(1 - 2 / out.r, 0)) / out.r);
  const thS = out.r > 3 ? Math.asin(sArg) : Math.PI - Math.asin(sArg);
  out.beta = smooth01(0.72, 0.9, p) * Math.min(Math.PI, thS + 0.5 * Math.atan(out.tanHalf));
  out.turn = smooth01(0.86, 0.95, p);         // grade only
  out.roll = (-2.5 + 4 * plunge) * D2R + lx * 1.5 * D2R;
  out.ignite = smooth01(0.03, 0.2, p);
  out.front = 5 + 26 * smooth01(0.04, 0.24, p);   // ignition front radius (M)
  out.sky = 1 - smooth01(0.05, 0.24, p);           // Milky Way glow: strong at arrival
  out.fade = 1 - smooth01(0.975, 1.0, p);
  return out;
}
